import { inject, Injectable } from '@angular/core';
import { APP_PATHS } from '@dev-fast/types';

import { LanguageService } from '@app/core/language-service';
import { LocalStorageService } from '@app/core/local-storage-service';

export const WELCOME_ENTRY_POINT = {
  welcome: 'welcome',
} as const;

export type WelcomeEntryPoint = (typeof WELCOME_ENTRY_POINT)[keyof typeof WELCOME_ENTRY_POINT];

const REDIRECT_PATH_BY_ENTRY_POINT: Record<WelcomeEntryPoint, string> = {
  [WELCOME_ENTRY_POINT.welcome]: APP_PATHS.welcome,
};

@Injectable({
  providedIn: 'root',
})
export class WelcomeEntryPointService {
  readonly #storage = inject(LocalStorageService);

  rememberFromUrl(url: string): void {
    const entryPoint = this.#resolveEntryPoint(url);

    if (!entryPoint) {
      return;
    }

    this.#storage.set('welcomeEntryPoint', entryPoint);
  }

  getRedirectPath(): string {
    const entryPoint = this.#storage.get('welcomeEntryPoint');

    if (!this.#isKnownEntryPoint(entryPoint)) {
      return APP_PATHS.welcome;
    }

    return REDIRECT_PATH_BY_ENTRY_POINT[entryPoint];
  }

  #resolveEntryPoint(url: string): WelcomeEntryPoint | null {
    const [firstSegment, secondSegment] = this.#getSegments(url);

    if (!firstSegment) {
      return null;
    }

    if (LanguageService.isAcceptedLanguage(firstSegment)) {
      return this.#resolveEntryPointByPath(secondSegment);
    }

    return this.#resolveEntryPointByPath(firstSegment);
  }

  #resolveEntryPointByPath(path?: string): WelcomeEntryPoint | null {
    if (path === APP_PATHS.welcome) {
      return WELCOME_ENTRY_POINT.welcome;
    }

    return null;
  }

  #isKnownEntryPoint(entryPoint?: string): entryPoint is WelcomeEntryPoint {
    return entryPoint === WELCOME_ENTRY_POINT.welcome;
  }

  #getSegments(url: string): string[] {
    const normalizedUrl = url
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+|\/+$/g, '');

    return normalizedUrl ? normalizedUrl.split('/') : [];
  }
}
